import { useEffect, useRef } from 'react';
import { API_CONFIG, getApiUrl } from '@/config/apiConfig';
import { fetchKioskInfo } from '@/services/kioskInfoCache';
import { buildRequestSigningHeaders } from '@/utils/requestSigning';

const HEARTBEAT_INTERVAL_DEFAULT_MS = 60 * 1000; // 60 seconds
const HEARTBEAT_INTERVAL_ENV = import.meta.env.VITE_KIOSK_HEARTBEAT_INTERVAL_SECONDS;
const HEARTBEAT_INTERVAL_MS = HEARTBEAT_INTERVAL_ENV
  ? Math.max(60, Number.parseInt(HEARTBEAT_INTERVAL_ENV, 10)) * 1000
  : HEARTBEAT_INTERVAL_DEFAULT_MS;

/**
 * Sends a POST /api/kiosk/heartbeat every 30 seconds so the backend
 * can detect when a kiosk goes offline and send a Telegram alert.
 *
 * On mount, first fetches /api/kiosk/info to confirm the current domain
 * is type "kiosk" before starting the interval. Mobile domains do not send heartbeat.
 * Fire-and-forget — failures are silently ignored so they never affect UX.
 */
export function useKioskHeartbeat() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const ping = async () => {
      try {
        const signingHeaders = await buildRequestSigningHeaders(
          'POST',
          API_CONFIG.ENDPOINTS.KIOSK.HEARTBEAT
        );
        await fetch(getApiUrl(API_CONFIG.ENDPOINTS.KIOSK.HEARTBEAT), {
          method: 'POST',
          credentials: 'include',
          headers: signingHeaders,
        });
      } catch {
        /* ignore */
      }
    };

    const start = async () => {
      try {
        const info = await fetchKioskInfo();
        const type = info?.type ?? '';
        if (type !== 'kiosk') return; // only kiosk sends heartbeat; mobile does not
      } catch {
        return; // can't determine domain type — skip heartbeat
      }

      if (cancelled) return;

      ping(); // immediate ping after domain confirmed
      intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    };

    start();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);
}
